/**
 * Phiếu khảo sát (TT 83 C9-c) — dùng chung cho trang trong app và link email (token).
 * Chặn gửi khi thiếu câu bắt buộc; nộp xong không sửa được.
 */
import { useState } from "react";
import { Alert, Button, Card, Input, Radio, Space, Typography } from "antd";

import {
  type AnswerInput,
  LIKERT_LABELS,
  type SurveyForm,
  missingRequired,
} from "@/features/surveys/api";
import { formatDateTime } from "@/lib/formatDate";

const { Title, Text, Paragraph } = Typography;

export function SurveyFormView({
  form,
  submitting,
  onSubmit,
}: {
  form: SurveyForm;
  submitting: boolean;
  onSubmit: (answers: AnswerInput[]) => void;
}) {
  const [values, setValues] = useState<Record<number, number | string>>({});
  const [missing, setMissing] = useState<number[]>([]);

  const answers: AnswerInput[] = form.questions
    .filter((q) => values[q.id] !== undefined && values[q.id] !== "")
    .map((q) =>
      q.kind === "text"
        ? { question: q.id, value_text: String(values[q.id]) }
        : { question: q.id, value_int: Number(values[q.id]) },
    );

  return (
    <Card>
      <Title level={3}>{form.title}</Title>
      <Paragraph type="secondary">
        {form.campaign_name}
        {form.course_code && ` · ${form.course_code} — ${form.course_name}`}
        {form.instructor_name && ` · Giảng viên: ${form.instructor_name}`}
        <br />
        Hạn trả lời: {formatDateTime(form.closes_at)}. Phiếu ẩn danh với giảng viên — giảng viên chỉ
        xem kết quả tổng hợp. Nộp xong không sửa được.
      </Paragraph>
      {!form.is_official_moet && (
        <Alert
          type="info"
          style={{ marginBottom: 16 }}
          message="Bộ câu hỏi tạm của nhà trường (chưa phải mẫu chính thức của Bộ GD&ĐT)."
        />
      )}
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {form.questions.map((q) => (
          <div key={q.id} data-testid={`question-${q.order}`}>
            <Text strong>
              {q.order}. {q.text}
              {q.required && <Text type="danger"> *</Text>}
            </Text>
            <div style={{ marginTop: 8 }}>
              {q.kind === "text" ? (
                <Input.TextArea
                  rows={3}
                  maxLength={4000}
                  value={(values[q.id] as string) ?? ""}
                  onChange={(e) => setValues({ ...values, [q.id]: e.target.value })}
                />
              ) : (
                <Radio.Group
                  value={values[q.id]}
                  onChange={(e) => setValues({ ...values, [q.id]: e.target.value })}
                >
                  <Space wrap>
                    {q.kind === "likert5"
                      ? [1, 2, 3, 4, 5].map((v) => (
                          <Radio key={v} value={v}>
                            {v} — {LIKERT_LABELS[v]}
                          </Radio>
                        ))
                      : q.choices.map((c, i) => (
                          <Radio key={i} value={i}>
                            {c}
                          </Radio>
                        ))}
                  </Space>
                </Radio.Group>
              )}
            </div>
          </div>
        ))}
        {missing.length > 0 && (
          <Alert type="warning" message={`Còn thiếu câu bắt buộc: ${missing.join(", ")}`} />
        )}
        <Button
          type="primary"
          loading={submitting}
          onClick={() => {
            const m = missingRequired(form.questions, answers);
            setMissing(m);
            if (m.length === 0) onSubmit(answers);
          }}
        >
          Nộp phiếu
        </Button>
      </Space>
    </Card>
  );
}
